import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { FlightTrack } from '../../types/flightTrack';
import { telemetryService } from '../../services/telemetryService';
import { ApiError } from '../../types/api';

/**
 * Where the selected drone has flown (#581, closing the last acceptance
 * criterion of epic #380).
 *
 * # Why a status union rather than loading / error flags
 *
 * This fetch has five outcomes and four of them are not "it worked":
 *
 * | status        | what the pilot is told                                |
 * |---------------|-------------------------------------------------------|
 * | `idle`        | nothing — no drone is selected                         |
 * | `loading`     | the track is being fetched                            |
 * | `flown`       | here is the path, with distance and duration           |
 * | `not-flown`   | this drone has no recorded flying in the last day      |
 * | `not-visible` | the server will not show it (404 — another pilot's)    |
 * | `failed`      | the request itself failed, and may be retried          |
 *
 * `not-flown` and `not-visible` are the two that matter, and a pair of booleans
 * cannot hold them apart: "no track" would mean both "this aircraft has not
 * flown" and "this is not your aircraft", and the first rendered in place of
 * the second is a statement about somebody else's fleet that we are not
 * entitled to make. The backend separates them deliberately — 200 with an
 * empty feature list against a 404 — and collapsing that here would throw away
 * the distinction it answers 404 to preserve (#382).
 */
export type FlightTrackStatus =
  | 'idle'
  | 'loading'
  | 'flown'
  | 'not-flown'
  | 'not-visible'
  | 'failed';

interface FlightTrackState {
  /** The path to draw, or null in every status but `flown`. */
  track: FlightTrack | null;
  status: FlightTrackStatus;
  /** The message the view shows when the fetch did not produce a track. */
  error: string | null;
  /**
   * The drone the current state describes.
   *
   * Kept so a response that arrives after the pilot has selected a different
   * aircraft can be discarded. Without it, a slow track for drone A draws
   * itself over drone B — a path on the map attributed to the wrong airframe,
   * which is the class of falsehood #575 was filed for.
   */
  droneId: string | null;
}

const initialState: FlightTrackState = {
  track: null,
  status: 'idle',
  error: null,
  droneId: null,
};

/** What a rejected fetch tells the reducer: refused, or merely broken. */
interface FlightTrackRejection {
  kind: 'not-visible' | 'failed';
  message: string;
}

export const fetchFlightTrack = createAsyncThunk<
  { droneId: string; track: FlightTrack | null },
  string,
  { rejectValue: FlightTrackRejection & { droneId: string } }
>('flightTrack/fetch', async (droneId, { rejectWithValue }) => {
  try {
    const track = await telemetryService.getFlightTrack(droneId);
    return { droneId, track };
  } catch (err) {
    const apiError = err as ApiError;
    if (apiError?.status === 404) {
      return rejectWithValue({
        droneId,
        kind: 'not-visible',
        message: 'This drone is not available to you.',
      });
    }
    return rejectWithValue({
      droneId,
      kind: 'failed',
      message:
        apiError?.serverMessage ||
        apiError?.message ||
        'Could not load the flight history.',
    });
  }
});

const flightTrackSlice = createSlice({
  name: 'flightTrack',
  initialState,
  reducers: {
    /** Forget the current track — on deselection, or before loading another. */
    clearFlightTrack: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchFlightTrack.pending, (state, action) => {
        state.status = 'loading';
        state.error = null;
        state.track = null;
        state.droneId = action.meta.arg;
      })
      .addCase(fetchFlightTrack.fulfilled, (state, action) => {
        // A response for a drone that is no longer the one being asked about is
        // stale. Dropping it is the point of keeping droneId.
        if (state.droneId !== action.payload.droneId) {
          return;
        }
        state.track = action.payload.track;
        state.status = action.payload.track ? 'flown' : 'not-flown';
        state.error = null;
      })
      .addCase(fetchFlightTrack.rejected, (state, action) => {
        const rejection = action.payload;
        if (rejection && state.droneId !== rejection.droneId) {
          return;
        }
        state.track = null;
        state.status = rejection?.kind ?? 'failed';
        state.error = rejection?.message ?? 'Could not load the flight history.';
      });
  },
});

export const { clearFlightTrack } = flightTrackSlice.actions;
export default flightTrackSlice.reducer;
